import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Universe } from '../../../types/content';
interface TiltCardProps {
  universe: Universe;
  size: 'hero' | 'standard' | 'wide';
  className?: string;
}
const spring = {
  stiffness: 220,
  damping: 22,
  mass: 0.6
};
export function TiltCard({
  universe,
  size,
  className = ''
}: TiltCardProps) {
  const ref = useRef<HTMLAnchorElement>(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), spring);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), spring);
  const imgX = useSpring(useTransform(mx, [-0.5, 0.5], ['3%', '-3%']), spring);
  const imgY = useSpring(useTransform(my, [-0.5, 0.5], ['3%', '-3%']), spring);
  const onPointerMove = (e: React.PointerEvent) => {
    if (reduce || e.pointerType !== 'mouse' || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onPointerLeave = () => {
    mx.set(0);
    my.set(0);
  };
  return <motion.div className={`[perspective:1200px] ${className}`} initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} data-id="element-223" data-mp="1f4ckvtf4gbtq">
      <Link ref={ref} to="/projets" onPointerMove={onPointerMove} onPointerLeave={onPointerLeave} aria-label={`${universe.title} — projets à venir`} className="group block h-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ember" data-id="element-224" data-mp="r888ihi47ten">
        <motion.div style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d'
      }} className="relative h-full bg-night" data-id="element-225" data-mp="2qkar7pix0jr9">
          <div className="absolute inset-0 overflow-hidden" data-id="element-226" data-mp="2k4tzls1hdymo">
            <motion.img src={universe.image} alt="" style={{
            x: imgX,
            y: imgY,
            scale: 1.12
          }} className="h-full w-full object-cover" data-id="element-227" data-mp="2k399qfq433v5" />
            <div className="absolute inset-0 bg-night/50 transition-colors duration-200 ease-out group-hover:bg-night/30" data-id="element-228" data-mp="2a8ha76cyy6xq" />
          </div>

          <div aria-hidden className="absolute right-0 top-0 grid grid-cols-3" style={{
          transform: 'translateZ(30px)'
        }} data-id="element-229" data-mp="32uii2lo5bml5">
            {[0, 1, 2, 3, 4, 5].map(i => <span key={i} className={`h-5 w-5 bg-ember opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 md:h-6 md:w-6 ${[1, 3, 4].includes(i) ? 'invisible' : ''}`} style={{
            transitionDelay: `${i * 35}ms`
          }} data-id="element-230" data-mp="2y1z59qxa8vhe" />)}
          </div>

          <div className="relative flex h-full flex-col justify-between p-6 text-white md:p-8" style={{
          transform: 'translateZ(50px)'
        }} data-id="element-231" data-mp="3u44vpu68zwl2">
            <div className="flex items-start gap-4" data-id="element-232" data-mp="6xkxj041h2em">
              <span className="font-mono text-sm text-ember" data-id="element-233" data-mp="7076932o1nyf">{universe.index}</span>
              <ul className="flex flex-wrap gap-1.5" data-id="element-234" data-mp="by8qhrbtvtbh">
                {universe.tags.map(tag => <li key={tag} className="bg-white/10 px-2 py-1 text-xs text-white/85 backdrop-blur-sm" data-id="element-235" data-mp="gvs4cq4m642w">
                    {tag}
                  </li>)}
              </ul>
            </div>

            <div data-id="element-236" data-mp="pqf1z041dc4l">
              <h3 className={`font-medium leading-[1.02] tracking-[-0.03em] ${size === 'hero' ? 'max-w-md text-3xl md:text-5xl' : 'max-w-sm text-2xl md:text-3xl'}`} data-id="element-237" data-mp="unf7n9wz4cv6">
                {universe.title}
              </h3>
              <p className={`mt-3 max-w-md leading-relaxed text-white/75 ${size === 'standard' ? 'line-clamp-2 text-sm' : 'text-base'}`} data-id="element-238" data-mp="14j8j0pyyi0bd">
                {universe.description}
              </p>
              <div className="mt-6 flex items-center justify-between border-t border-white/15 pt-4" data-id="element-239" data-mp="2b1hvjeksk8jc">
                <span className="text-sm text-white/70" data-id="element-240" data-mp="26487a29522x3">Voir les projets · bientôt</span>
                <span className="flex h-9 w-9 items-center justify-center bg-white text-ink transition-colors duration-150 group-hover:bg-ember group-hover:text-white" data-id="element-241" data-mp="1iex288kr9md5">
                  <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:rotate-45" data-id="element-242" data-mp="1rfbpvel6zdjt" />
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </Link>
    </motion.div>;
}